/* eslint-disable no-throw-literal */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import clubAPI from "../api/clubAPI";
import "../App.scss";
import swalDialog from "../helpers/swalErrorPopup";
import FreeFitDefaultImg from "../img/freefit_default.png";
import FreeFitLeumitDefaultImg from "../img/default_freefit_leumit.png";
import FreeFitMaoflaamitDefaultImg from "../img/default_freefit_maoflaamit.png";
import HistoryDefImg from "../img/history_def_img.png";
import dropdownIcon from "../img/svg/dropdown_icon.svg";
import navIcon from "../img/svg/navigation_icon.svg";
import CustomSearch from "../shared/CustomSearch";
import Dropdown from "../shared/Dropdown";
import ImageWithFallback from "../shared/imageWithFallback";
import { PoweredBy } from "../shared/PoweredBy";
import { updateAllAppParams, updateAllClubParams } from "../store/appParamSlice";
import { footerCustomDisplaying, headerCustomDisplaying, isLoadChanger, setCustomHeaderText } from "../store/mainSlice";
import { isPartnerClalit, isPartnerLeumit, isPartnerMehudet, isPartnerOvdimBari, isPartnerMaoflaamit, selectIsOrthodox, selectPartner } from "../store/partnerStore";
import { isV2Enabled, V2_SCREEN_KEYS } from "../v2/registry";
import { getPartnerAsset } from "../v2/theme/partnerAssets";
import FitExtendSelectorV2 from "../v2/screens/fitExtendSelector/FitExtendSelector";

function FitExtendSelector() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const partner = useSelector(selectPartner);
  const isClalit = useSelector(isPartnerClalit);
  const isLeumit = useSelector(isPartnerLeumit);
  const isOvdimbari = useSelector(isPartnerOvdimBari);
  const isMaoflaamit = useSelector(isPartnerMaoflaamit);
  const isMeuhedat = useSelector(isPartnerMehudet);
  const isOrthodox = useSelector(selectIsOrthodox);
  const [detailedClubParams, setDetailedClubParams] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedCityObject, setSelectedCityObject] = useState();
  const [selectedClub, setSelectedClub] = useState("");
  const [selectedClubCategory, setSelectedClubCategory] = useState("");

  const userParams = useSelector((state) => state.userSlice.userData);
  const mainUserParams = useSelector((state) => state.userSlice.mainUserData);
  const cityAndClubs = useSelector((state) => state.appParamSlice.allAppParams);
  const clubCategories = useSelector((state) => state.appParamSlice.clubCategories);
  const navigate = useNavigate();

  const [allClubs, setAllClubs] = useState([]);

  // Answers that do not change while this screen is open, remembered so the dropdowns stop re-firing
  // endpoints that cost several database round trips each. Refs, not state: reading them must not render.
  const categoryClubIdsRef = useRef(new Map());
  const cityCategoriesRef = useRef(new Map());

  useEffect(() => {
    if (Object.keys(cityAndClubs).length > 0) {
      setAllClubs(cityAndClubs.ClubList);
    }
  }, [cityAndClubs]);

  useEffect(() => {
    const fetch = async () => {
      try {
        dispatch(isLoadChanger(true));
        const credentials = {
          binID: userParams.BinID,
          token: mainUserParams.Token,
          id: mainUserParams.ID,
          phone: mainUserParams.Phone,
        };
        // V2 drives its category chips off a static list, so it needs neither
        // this call nor its error path (which would otherwise dialog over a V2
        // screen about data V2 never reads).
        const needsCategories = !isV2Enabled(V2_SCREEN_KEYS.FIT_EXTEND_SELECTOR, partner);
        // The two calls are independent, so they go out together rather than one after the other.
        // allSettled rather than all, so the payload error still wins over the category error the way
        // it did when the second call only happened after the first had succeeded.
        const [fullDataResult, categoriesResult] = await Promise.allSettled([
          clubAPI.getWebFullData(credentials),
          needsCategories ? clubAPI.getClubCategoryList({ ...credentials, isFreefitWeb: true }) : null,
        ]);
        if (fullDataResult.status === "rejected") {
          throw fullDataResult.reason;
        }
        const param = fullDataResult.value;
        if (param.data.Error !== 0) {
          throw {
            code: param.data.Error,
            mess: param.data.Message,
          };
        }

        // Data is ~1 MB of JSON for ~900 clubs; parse it once and share the result.
        const clubParam = JSON.parse(param.data.Data);
        dispatch(updateAllAppParams(clubParam));
        if (needsCategories) {
          if (categoriesResult.status === "rejected") {
            dispatch(isLoadChanger(false));
            throw categoriesResult.reason;
          }
          const clubResp = categoriesResult.value;
          if (clubResp.data.Error !== 0) {
            dispatch(isLoadChanger(false));
            throw {
              code: clubResp.data.Error,
              mess: clubResp.data.Message,
            };
          }
          dispatch(updateAllClubParams(JSON.parse(clubResp.data.Data)));
        }
        setDetailedClubParams(clubParam?.ClubList);

        dispatch(isLoadChanger(false));
      } catch (err) {
        swalDialog(err);
      }
    };
    if (userParams && Object.keys(userParams).length > 0) {
      // V2 reaches this screen from the home page, which already put
      // GetWebFullData's payload in appParamSlice — reuse it instead of paying
      // for the same call twice. The effect below fills the list from the store.
      const cachedForV2 =
        isV2Enabled(V2_SCREEN_KEYS.FIT_EXTEND_SELECTOR, partner) && Object.keys(cityAndClubs).length > 0;
      if (cachedForV2) {
        // nothing to fetch
      } else if (isClalit || isLeumit || isOvdimbari || isMaoflaamit || isMeuhedat) {
        fetch();
      } else if (clubCategories.length === 0 || Object.keys(cityAndClubs).length === 0) {
        fetch();
      }
    }
  }, [userParams]);

  useEffect(() => {
    const fetch = async () => {
      let clubs = cityAndClubs?.ClubList;
      // ClubList is undefined until GetWebFullData lands.
      if (!Array.isArray(clubs)) return;
      if (selectedCity) {
        clubs = clubs.filter((el) => el.AreaName.includes(selectedCity));
      }
      if (selectedClub) {
        clubs = clubs.filter((el) => el.RecordID == selectedClub);
      }
      if (selectedClubCategory) {
        try {
          // This effect re-runs on every city and club change, but the answer depends only on the
          // category (plus the city for non-Clalit partners), so it is remembered per pair rather than
          // re-firing an endpoint that costs several database round trips per call.
          const cityId = isClalit ? "" : selectedCityObject?.value;
          const cacheKey = selectedClubCategory.value + "|" + (cityId ?? "");
          let allowedClubIds = categoryClubIdsRef.current.get(cacheKey);
          if (!allowedClubIds) {
            dispatch(isLoadChanger(true));
            const payload = {
              binID: userParams.BinID,
              mainCatID: selectedClubCategory.value,
              token: mainUserParams.Token,
              id: mainUserParams.ID,
              phone: mainUserParams.Phone,
            };
            if (!isClalit) {
              payload.cityId = selectedCityObject.value;
              payload.isFreefitWeb = true;
            }
            const clubsInCategory = await clubAPI.getClubsByCategory(payload);
            // A Set, not the raw array: the filter below tests it once per club in the list.
            allowedClubIds = new Set(JSON.parse(clubsInCategory.data.Data).ClubsList);
            categoryClubIdsRef.current.set(cacheKey, allowedClubIds);
            dispatch(isLoadChanger(false));
          }
          clubs = clubs.filter((el) => allowedClubIds.has(el.RecordID));
        } catch (err) {
          dispatch(isLoadChanger(false));
          swalDialog(err);
        }
      }
      setDetailedClubParams(clubs);
      setAllClubs(clubs);
    };
    fetch();
    // cityAndClubs: re-runs the filter once the club list arrives.
  }, [selectedCity, selectedClub, selectedClubCategory, cityAndClubs]);

  useEffect(() => {
    dispatch(headerCustomDisplaying(true));
    dispatch(footerCustomDisplaying(true));
    dispatch(setCustomHeaderText(t("COMPLETE_CLUB_LIST")));
    return () => {
      dispatch(headerCustomDisplaying(false));
      dispatch(footerCustomDisplaying(false));
      dispatch(setCustomHeaderText(""));
    };
  }, [t]);

  const handleSelectCity = async (selectedOption) => {
    setSelectedCity(selectedOption?.label);
    setSelectedCityObject(selectedOption);
    if (!isClalit) {
      // A city keeps its category list while the screen is open, and this handler also fires when the
      // dropdown is merely opened, so re-picking a city costs nothing after the first time.
      const cityId = selectedOption?.value;
      const remembered = cityId ? cityCategoriesRef.current.get(cityId) : undefined;
      if (remembered) {
        dispatch(updateAllClubParams(remembered));
        return;
      }
      const param = await clubAPI.getClubsByCategoryByCityId({
        binID: userParams.BinID,
        id: mainUserParams.ID,
        token: mainUserParams.Token,
        phone: mainUserParams.Phone,
        cityId: selectedOption.value,
      });
      if (param.data.Error !== 0) {
        throw {
          code: param.data.Error,
          mess: param.data.Message,
        };
      }
      const cityCategories = JSON.parse(param.data.Data);
      if (cityId) {
        cityCategoriesRef.current.set(cityId, cityCategories);
      }
      dispatch(updateAllClubParams(cityCategories));
    }
  };

  const handleSelectClub = (selectedOption) => {
    setSelectedClub(selectedOption?.value);
  };

  const handleSelectClubCategory = (selectedOption) => {
    setSelectedClubCategory(selectedOption);
  };

  const handleSearch = () => {};

  if (isV2Enabled(V2_SCREEN_KEYS.FIT_EXTEND_SELECTOR, partner)) {
    return (
      <FitExtendSelectorV2
        clubs={detailedClubParams}
        cityOptions={
          cityAndClubs?.CityList && Array.isArray(cityAndClubs?.CityList) && cityAndClubs?.CityList.length > 0
            ? cityAndClubs?.CityList.map((el) => ({
                value: el.RecordID,
                label: el.Name,
              }))
            : []
        }
        onCitySelect={handleSelectCity}
        isOrthodox={isOrthodox}
        defaultImg={getPartnerAsset(partner, "defaultClubImg")}
        imgFallbackSrc={getPartnerAsset(partner, "clubFallbackImg")}
        showPoweredBy={isClalit}
      />
    );
  }

  return (
    <div className="main-page-container">
      {isClalit || isMaoflaamit ? (
        <div className="fit-extend-selector-dropdowns-container">
          <Dropdown
            options={
              cityAndClubs?.CityList && Array.isArray(cityAndClubs?.CityList) && cityAndClubs?.CityList.length > 0
                ? cityAndClubs?.CityList.map((el) => ({
                    value: el.RecordID,
                    label: el.Name,
                  }))
                : []
            }
            onSelect={handleSelectCity}
            imgUrl={navIcon}
            placeholder={t("SELECT_SEARCH_CITY")}
          />
          <Dropdown
            options={
              allClubs && Array.isArray(allClubs) && allClubs.length > 0
                ? allClubs?.map((el) => ({
                    value: el.RecordID,
                    label: el.Name,
                  }))
                : []
            }
            onSelect={handleSelectClub}
            imgUrl={dropdownIcon}
            isShouldRotate={true}
            placeholder={t("SELECT_SEARCH_CLUB")}
          />
          <Dropdown
            options={
              clubCategories && Array.isArray(clubCategories) && clubCategories.length > 0
                ? clubCategories.map((el) => ({
                    value: el.ID,
                    label: el.Name,
                  }))
                : []
            }
            onSelect={handleSelectClubCategory}
            imgUrl={dropdownIcon}
            isShouldRotate={true}
            placeholder={t("SEARCH_BY_CATEGORY")}
          />
        </div>
      ) : (
        <CustomSearch
          cityOptions={
            cityAndClubs?.CityList && Array.isArray(cityAndClubs?.CityList) && cityAndClubs?.CityList.length > 0
              ? cityAndClubs?.CityList.map((el) => ({
                  value: el.RecordID,
                  label: el.Name,
                }))
              : []
          }
          clubOptions={
            allClubs && Array.isArray(allClubs) && allClubs.length > 0
              ? allClubs?.map((el) => ({
                  value: el.RecordID,
                  label: el.Name,
                }))
              : []
          }
          categoryOptions={
            clubCategories && Array.isArray(clubCategories) && clubCategories.length > 0
              ? clubCategories.map((el) => ({
                  value: el.ID,
                  label: el.Name,
                }))
              : []
          }
          onCitySelect={handleSelectCity}
          onClubSelect={handleSelectClub}
          onCategorySelect={handleSelectClubCategory}
        />
      )}

      <div className="fit-extend-selector-elements-container">
        {detailedClubParams && Array.isArray(detailedClubParams) && detailedClubParams.length > 0
          ? detailedClubParams.map((el, i) => (
              <div
                className="fit-extend-selector-card"
                key={i}
                onClick={(e) => {
                  navigate(`/venue/${el.RecordID}`, {
                    state: {
                      price: el.Price,
                      clubId: el.RecordID,
                      clubName: el.Name,
                    },
                  });
                }}
              >
                <div className="fit-extend-selector-content-wrapper">
                  <div className="fit-extend-selector-left">
                    {el.Price > 0 ? (
                      <p className="fit-extend-selector-price"> ₪{el.Price}</p>
                    ) : (
                      <p className="fit-extend-selector-price"> </p>
                    )}
                    <button className={isMaoflaamit ? "fit-extend-maof-card-btn" : "select-room-by-date-main-element-btn-coloured"}>{t("TO_BOOK")}</button>
                  </div>

                  <div className="fit-extend-selector-content-body">
                    <div className="fit-extend-selector-content-adress-and-name">
                      <p className="fit-extend-selector-content-header-text">{el.Name}</p>
                      <p className="fit-extend-selector-content-body-first">{el.Address}</p>
                    </div>
                  </div>
                </div>
                <div className="select-room-by-date-club-img-wrapper">
                  <ImageWithFallback
                    loading="lazy"
                    decoding="async"
                    src={isOrthodox ? FreeFitDefaultImg : el.MobClubLogoPath}
                    fallbackSrc={isClalit ? HistoryDefImg : 
                                 isLeumit ? FreeFitLeumitDefaultImg : 
                                 isMaoflaamit ? FreeFitMaoflaamitDefaultImg :
                                 FreeFitDefaultImg}
                    alt="Club logo"
                  />
                </div>
              </div>
            ))
          : ""}
      </div>

      {isClalit && (
        <div style={{ position: "sticky", bottom: 0 }}>
          <PoweredBy />
        </div>
      )}
    </div>
  );
}

export default FitExtendSelector;
